const get = require('lodash/get')
const { getServiceForLocationId } = require('../../service/tools/territory-lookup')
const { parseTextDate } = require('../../shipment/tools/safe-date-parser')
const getPeriodFromProgram = require('../../report/tools/get-period-from-program')
const createReport = require('../../report/tools/create')
const { bulkUpsert } = require('../../report/data-access/pouchdb-adapter')
const { sanitiseBatchNo } = require('../../batch/tools')

function createInventoryAdjustment ({ mainApi, logger, batches }) {
  return async function inventoryAdjustment (params) {
    if (!params) {
      const err = new Error('inventoryAdjustment: payload is required')
      err.status = 400
      throw err
    }

    const adjustments = Array.isArray(params.inventory_adjustment_data) ? params.inventory_adjustment_data : []
    if (!adjustments.length) {
      const err = new Error('inventoryAdjustment: warehouse and adjustment payload are required')
      err.status = 400
      throw err
    }

    // Group adjustments to avoid reloading ledger per SKU.
    const adjustmentsByWarehouse = new Map()
    const isBulkUpload = adjustments[0].reason_code === 'Bulk Upload'
    adjustments.forEach(adjustment => {
      if (!adjustment || !adjustment.warehouse) {
        const err = new Error('inventoryAdjustment: warehouse and adjustment payload are required')
        err.status = 400
        throw err
      }

      if (!adjustment.sku_code) {
        const err = new Error('inventoryAdjustment: sku_code is required')
        err.status = 400
        throw err
      }

      const adjustedQuantityValue = adjustment.adjusted_quantity
      const adjustedQuantity = Number(adjustedQuantityValue)
      if (!Number.isFinite(adjustedQuantity)) {
        const err = new Error('inventoryAdjustment: adjusted_quantity is required')
        err.status = 400
        throw err
      }

      if (!adjustmentsByWarehouse.has(adjustment.warehouse)) {
        adjustmentsByWarehouse.set(adjustment.warehouse, [])
      }

      adjustmentsByWarehouse.get(adjustment.warehouse).push({
        ...adjustment,
        adjustedQuantity
      })
    })

    for (const [warehouseCode, warehouseAdjustments] of adjustmentsByWarehouse.entries()) {
      // Lookup location + service once per warehouse.
      const location = await mainApi.location.pgAdapter.getOneByCode(warehouseCode)
      const locationId = location.fsid

      const territory = getServiceForLocationId(locationId)
      const serviceId = typeof territory === 'string'
        ? territory
        : (territory && (territory.service || territory.id))

      if (!serviceId) {
        if (logger && logger.warn) {
          logger.warn('inventoryAdjustment: skipping adjustment due to missing service', {
            warehouse: warehouseCode,
            locationId,
            sku_code: warehouseAdjustments[0].sku_code
          })
        }
        continue
      }

      const service = await mainApi.service.get(serviceId)

      let ledger
      try {
        ledger = await mainApi.stock.getLedgerBalanceAsReport({
          locationId,
          service,
          excludeProducts: 'configuration',
          calculateFields: true,
          api: mainApi,
          usePGLedger: true
        })
      } catch (err) {
        throw new Error(`inventoryAdjustment: unable to load ledger for ${locationId}: ${err.message || err}`)
      }

      if (!ledger || !ledger.stock) {
        throw new Error('inventoryAdjustment: ledger did not include stock data')
      }

      const productKeys = new Set(warehouseAdjustments.map(item => `product:${item.sku_code}`))
      const totalsBySku = new Map()
      const stockFromLedger = ledger.stock || {}
      const clonedStock = Object.keys(stockFromLedger).reduce((acc, key) => {
        if (!productKeys.has(key)) return acc
        const product = stockFromLedger[key] || {}
        acc[key] = {
          ...product,
          fields: { ...(product.fields || {}) }
        }
        if (product.batches) {
          acc[key].batches = { ...product.batches }
        }
        return acc
      }, {})

      for (const adjustment of warehouseAdjustments) {
        const productKey = `product:${adjustment.sku_code}`
        const updatedQuantityValue = Number(adjustment.updated_quantity)
        const deltaValue = isBulkUpload ? updatedQuantityValue : adjustment.adjustedQuantity
        const currentTotal = totalsBySku.get(adjustment.sku_code) || 0
        totalsBySku.set(adjustment.sku_code, currentTotal + deltaValue)

        if (!clonedStock[productKey]) {
          clonedStock[productKey] = { fields: {} }
        }

        // We get existing count and sum/deduct adjusted quantity
        // But for bulk uploads we just set to updated quantity
        // Product-level balances are updated after batch processing.

        const batchNumberRaw = adjustment.batch_number
        const batchNumber = batchNumberRaw ? sanitiseBatchNo(batchNumberRaw) : 'UNKNOWN'
        if (batchNumber) {
          const manufacturer = 'unknown'
          const expiryDate = parseTextDate(adjustment.expiry_date)

          const batchKey = `${productKey}:manufacturer:${manufacturer}:batchNo:${batchNumber}`
          const currentBatch = get(clonedStock[productKey], ['batches', batchKey], {})
          const currentBatchFields = currentBatch.fields || {}

          clonedStock[productKey].batches = clonedStock[productKey].batches || {}
          clonedStock[productKey].batches[batchKey] = {
            ...currentBatch,
            fields: {
              ...currentBatchFields,
              'field:standard-physical-count': { amount: updatedQuantityValue }
            }
          }

          await batches.upsertBatchDetails({
            skuCode: adjustment.sku_code,
            batchNo: batchNumber,
            manufacturer,
            expiry: expiryDate || new Date('2099-12-31').toJSON()
          })
        }
      }

      // Update product-level balances using summed values per SKU.
      totalsBySku.forEach((total, skuCode) => {
        const productKey = `product:${skuCode}`
        if (!clonedStock[productKey]) {
          clonedStock[productKey] = { fields: {} }
        }
        const existingQuantity = (clonedStock[productKey].fields['field:standard-physical-count'] &&
          Number(clonedStock[productKey].fields['field:standard-physical-count'].amount)) || 0
        const newQuantity = isBulkUpload ? total : existingQuantity + total
        clonedStock[productKey].fields = clonedStock[productKey].fields || {}
        clonedStock[productKey].fields['field:standard-physical-count'] = { amount: newQuantity }
        clonedStock[productKey].fields['field:shelflife-balance'] = { amount: newQuantity }
      })

      // Stamp standard-remark on all payload products.
      productKeys.forEach(productKey => {
        if (clonedStock[productKey]) {
          clonedStock[productKey].fields['field:standard-remark'] = { amount: 'Shipsy-inventory-adjustment' }
        }
      })

      // Create one partial count per warehouse for the batch of adjustments.
      const now = new Date().toISOString()
      const referenceDate = parseTextDate(warehouseAdjustments[0].adjusted_at || warehouseAdjustments[0].created_at) || now
      const period = getPeriodFromProgram(service.program, referenceDate, true)
      const username = warehouseAdjustments[0].adjusted_by

      const stockCountDoc = createReport({
        locationId,
        service,
        period,
        username,
        createdAt: now,
        submittedAt: now,
        stock: clonedStock,
        partialCount: true
      })

      stockCountDoc.documents = ledger.documents || {}
      stockCountDoc.partialCount = true

      await bulkUpsert(mainApi.state, [stockCountDoc])

      if (logger && logger.info) {
        logger.info('inventoryAdjustment: created partial count from Shipsy payload', {
          warehouse: warehouseCode,
          sku_codes: warehouseAdjustments.map(item => item.sku_code),
          reportId: stockCountDoc._id
        })
      }
    }
  }
}

module.exports = createInventoryAdjustment
